import { useEffect } from "react";

interface Shortcut {
  /** Key to listen for (e.g. "k", "/", "Escape") */
  key: string;
  /** Require Ctrl (or Cmd on macOS) */
  ctrl?: boolean;
  /** Require Shift */
  shift?: boolean;
  /** Handler to invoke */
  handler: (e: KeyboardEvent) => void;
}

/**
 * Register one or more global keyboard shortcuts.
 *
 * Shortcuts are ignored when the user is typing in an input, textarea,
 * or contentEditable element.
 *
 * Usage:
 * ```ts
 * useHotkeys([
 *   { key: "k", ctrl: true, handler: () => openSearch() },
 *   { key: "/", handler: () => focusSearch() },
 *   { key: "Escape", handler: () => close() },
 * ]);
 * ```
 */
export function useHotkeys(shortcuts: Shortcut[]) {
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Ignore shortcuts when typing
      const tag = (e.target as HTMLElement)?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        return;
      }

      for (const s of shortcuts) {
        const ctrlMatch = s.ctrl
          ? e.ctrlKey || e.metaKey
          : !e.ctrlKey && !e.metaKey;
        const shiftMatch = s.shift ? e.shiftKey : !e.shiftKey;

        if (e.key === s.key && ctrlMatch && shiftMatch) {
          e.preventDefault();
          s.handler(e);
          return;
        }
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [shortcuts]);
}
